import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { zodResolver } from '@hookform/resolvers/zod';
import { useRpcRouter, useSettings } from '@nest/common-core-frontend-react';
import { Button, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, Form, FormControl, FormField, FormItem, FormLabel, FormMessage, TextNumber } from '@nest/uikit-react';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { PrinterAutocomplete } from './PrinterAutocomplete';
export function PrinterSelectorDialog({ identifier, numberOfCopies = 1, allowedActions = ['download', 'print', 'preview'], initialPrinterIds = [], onPrintersChange, onSubmit, onClose }) {
    const [open, setOpen] = useState(true);
    const [selectedPrinter, setSelectedPrinter] = useState(null);
    const { apiUrl } = useSettings();
    const printersClient = useRpcRouter(apiUrl);
    const initialPrintersQuery = useQuery({
        queryKey: ['printers', 'by-ids', (initialPrinterIds ?? []).join(',')],
        queryFn: async () => {
            const ids = initialPrinterIds ?? [];
            if (ids.length === 0) {
                return [];
            }
            const result = await printersClient.printers.getAll.execute({
                filter: { id: { $in: ids } },
                limit: ids.length,
                offset: 0
            });
            return result ?? [];
        },
        enabled: (initialPrinterIds?.length ?? 0) > 0
    });
    useEffect(() => {
        if ((initialPrintersQuery.data?.length ?? 0) === 0) {
            return;
        }
        const ids = initialPrinterIds ?? [];
        const preferredId = ids[0];
        const preferred = initialPrintersQuery.data?.find(p => p.id === preferredId) ?? initialPrintersQuery.data?.[0] ?? null;
        setSelectedPrinter(preferred ?? null);
    }, [initialPrintersQuery.data, initialPrinterIds]);
    const schema = z.object({
        printer: z
            .object({
            id: z.number()
        })
            .optional(),
        numberOfCopies: z.number({ error: 'Zadejte počet kopií' }).min(1, { message: 'Minimálně 1 kopie' })
    });
    const form = useForm({
        resolver: zodResolver(schema),
        defaultValues: {
            printer: undefined,
            numberOfCopies: numberOfCopies ?? 1
        }
    });
    useEffect(() => {
        if (selectedPrinter) {
            form.setValue('printer', selectedPrinter, { shouldDirty: false, shouldTouch: false });
        }
    }, [selectedPrinter, form]);
    const printer = form.watch('printer');
    const disablePrintButton = useMemo(() => {
        return printer == null;
    }, [printer]);
    const handleClose = () => {
        setOpen(false);
        onClose();
    };
    const handleAction = (action) => form.handleSubmit(values => {
        onSubmit({
            action,
            numberOfCopies: values.numberOfCopies ?? 1,
            printerIds: values.printer != null ? [values.printer.id] : []
        });
        handleClose();
    })();
    const handlePreview = () => {
        const numberOfCopies = form.getValues('numberOfCopies') ?? 1;
        onSubmit({ action: 'preview', numberOfCopies, printerIds: [] });
        handleClose();
    };
    return (_jsx(Dialog, { open: open, onOpenChange: handleClose, children: _jsxs(DialogContent, { className: "desktop:w-[400px] p-4", children: [_jsx(DialogHeader, { children: _jsx(DialogTitle, { children: "Tisk" }) }), _jsx(Form, { ...form, children: _jsxs("form", { className: "flex flex-col gap-4", onSubmit: e => e.preventDefault(), children: [_jsx(FormField, { control: form.control, name: "printer", render: () => (_jsxs(FormItem, { children: [_jsx(FormLabel, { children: "Tisk\u00E1rna" }), _jsx(FormControl, { children: _jsx(PrinterAutocomplete, { value: selectedPrinter, onChange: val => {
                                                    const selected = val ?? null;
                                                    setSelectedPrinter(selected);
                                                    form.setValue('printer', selected ?? undefined, { shouldDirty: true, shouldTouch: true });
                                                    if (onPrintersChange) {
                                                        onPrintersChange(selected ? [selected] : []);
                                                    }
                                                }, onPrintersChange: onPrintersChange }) }), _jsx(FormMessage, {})] })) }), _jsx(FormField, { control: form.control, name: "numberOfCopies", render: ({ field }) => (_jsxs(FormItem, { children: [_jsx(FormLabel, { children: "Po\u010Det kopi\u00ED" }), _jsx(FormControl, { children: _jsx(TextNumber, { placeholder: "Zadejte po\u010Det kopi\u00ED", value: field.value ?? 1, onChange: v => field.onChange(v ?? 1) }) }), _jsx(FormMessage, {})] })) })] }) }), _jsxs(DialogFooter, { className: "mt-4 gap-2", children: [_jsx(Button, { variant: "default", onClick: () => handleAction('print'), disabled: disablePrintButton, children: "Tisknout" }), allowedActions.includes('preview') && (_jsx(Button, { variant: "outline", onClick: handlePreview, children: "N\u00E1hled" })), allowedActions.includes('download') && (_jsx(Button, { variant: "outline", onClick: () => handleAction('download'), disabled: disablePrintButton, children: "St\u00E1hnout" }))] })] }) }));
}
//# sourceMappingURL=PrinterSelectorDialog.js.map